import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetEmployeeData, setEmployeeData } from "./actions/employeeSlice";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";
import { useNavigate, useParams } from "react-router-dom";
import z from "zod";
import useDebounce from "~/hooks/useDebounce";
import { triggerGlobalToast } from "~/utils/globalToast";

const useEmployeeRegistry = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setIsLoading] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [employees, setEmployees] = useState([]);
  const debouncedSearchTerm = useDebounce(searchQuery, 500);

  const {
    activeIndex,
    onboardBtnEnable,
    nextBtnEnable,
    showOnboardBtn,
    status,
    isLeaveModal,
    personalInformation,
    address,
    employmentInformation,
    bankInformation,
  } = useSelector((state) => state.employeeregister);
  const handleNext = () => {
    dispatch(
      setEmployeeData({ key: "activeIndex", value: Number(activeIndex) + 1 }),
    );
  };
  const handleBack = () => {
    dispatch(
      setEmployeeData({ key: "activeIndex", value: Number(activeIndex) - 1 }),
    );
  };
  const addNewEmployee = () => {
    setSubmit(false);
    dispatch(resetEmployeeData());
  };
  const handleClose = () => {
    dispatch(resetEmployeeData());
    navigate(`/entity/${id}/employee`);
  };
  useEffect(() => {
    handleClearState();
  }, []);

  const handleClearState = () => {
    dispatch(
      setEmployeeData({
        key: "personalInformation",
        value: {
          given_name: "",
          other_name: "",
          last_name: "",
          preferred_name: "",
          tax_identification_no: "",
          national_id: "",
          social_security_number: "",
          personal_email: "",
          work_email: "",
          phone_code: "",
          phone: "",
        },
      }),
    );
    dispatch(
      setEmployeeData({
        key: "address",
        value: {
          country: "",
          address_line_1: "",
          address_line_2: "",
          state: "",
          city: "",
          postalcode: "",
        },
      }),
    );
    dispatch(
      setEmployeeData({
        key: "employmentInformation",
        value: {
          employee_identifier: "",
          start_date: null,
          end_date: null,
          employment_type: "",
          employment_status: "",
          job_title: "",
          cost_center: "",
          currency: "",
          salary: "",
          compensation_type: "",
        },
      }),
    );
    dispatch(
      setEmployeeData({
        key: "bankInformation",
        value: {
          bank_account_holder_name: "",
          bank_name: "",
          bank_account_number: "",
          bank_swift_bic: "",
          bank_account_currency: "",
          bank_branch_address: "",
          bank_country: "",
        },
      }),
    );
    setSubmit(false);
    dispatch(setEmployeeData({ key: "activeIndex", value: 0 }));
    dispatch(setEmployeeData({ key: "isLeaveModal", value: false }));
  };
  const handleSave = async () => {
    let data = {
      name: personalInformation.given_name || "",
      other_name: personalInformation.other_name || "",
      last_name: personalInformation.last_name || "",
      preferred_name: personalInformation.preferred_name || "",
      tax_id_number: personalInformation.tax_identification_no || "",
      national_id: personalInformation.national_id || null,
      ssn: personalInformation.social_security_number || "",
      ...(personalInformation.marital_status &&
        personalInformation.marital_status.trim() !== "" && {
          marital_status: personalInformation.marital_status,
        }),
      personal_email: personalInformation.personal_email || "",
      work_email: personalInformation.work_email || "",
      phone_number:
        `${personalInformation.phone_code}-${personalInformation.phone}` || "",
      address_line1: address.address_line_1 || "",
      address_line2: address.address_line_2 || "",
      city: address.city?.code || "",
      country: address.country?.code || "",
      state_province: address.state?.code || "",
      zip_code: address.postalcode || "",
      employee_identifier: employmentInformation.employee_identifier || "",
      start_date: employmentInformation.start_date || "",
      ...(employmentInformation.end_date && {
        end_date: employmentInformation.end_date,
      }),
      ...(employmentInformation.employment_type &&
        employmentInformation.employment_type.trim() !== "" && {
          employment_type: employmentInformation.employment_type,
        }),
      ...(employmentInformation.employment_status &&
        employmentInformation.employment_status.trim() !== "" && {
          employment_status: employmentInformation.employment_status,
        }),
      job_title: employmentInformation.job_title || "",
      cost_center: employmentInformation.cost_center || "",
      ...(employmentInformation.compensation_type &&
        employmentInformation.compensation_type.trim() !== "" && {
          compensation_type: employmentInformation.compensation_type,
        }),
      salary_currency: employmentInformation.currency || "",
      salary_amount: employmentInformation.salary || "",
      bank_account_holder_name: bankInformation.bank_account_holder_name || "",
      bank_name: bankInformation.bank_name || "",
      bank_account_number: bankInformation.bank_account_number || "",
      bank_branch_address: bankInformation.bank_branch_address || "",
      bank_swift_bic: bankInformation.bank_swift_bic || "",
      ...(bankInformation.bank_account_currency &&
        bankInformation.bank_account_currency.trim() !== "" && {
          bank_account_currency: bankInformation.bank_account_currency,
        }),
      bank_country: bankInformation.bank_country?.code || null,
    };
    try {
      setIsLoading(true);
      await apiService(API_ROUTES.CERATE_EMPLOYEE, "POST", {
        body: JSON.stringify(data),
        replace: { id },
      });
      setIsLoading(false);
      setSubmit(true);
      dispatch(setEmployeeData({ key: "isLeaveModal", value: false }));
    } catch (error) {
      setSubmit(false);
      console.error(error);
      setIsLoading(false);
      const err = error as Error & { status?: number; statusText?: string };
      const errorMessage =
        err.message || "Failed to update personal information";
      triggerGlobalToast({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 5000,
      });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const fetchEmployees = async ({
    pageIndex = page,
    pageSize = rows,
    sortBy,
    sortOrder,
  }: {
    pageIndex?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: string;
  } = {}) => {
    setIsLoading(true);
    setIsDataAvailable(true);
    try {
      const { data } = await apiService(API_ROUTES.FETCH_EMPLOYEES, "GET", {
        replace: {
          id,
          page: pageIndex + 1,
          pageSize: pageSize,
          search: searchQuery,
          sortBy: sortBy || "name",
          sortOrder: sortOrder || "ASC",
        },
        schema: z.object({
          id: z.string().uuid(),
          page: z.number().optional(),
          pageSize: z.number().optional(),
          search: z.string().optional(),
          sortBy: z
            .enum([
              "employee_identifier",
              "name",
              "work_email",
              "job_title",
              "employment_status",
              "start_date",
              "end_date",
            ])
            .optional(),
          sortOrder: z.enum(["ASC", "DESC"]).optional(),
        }),
      });
      const employeeList = data?.records || [];
      setTotalRecords(data?.totalRecords || 0);

      if (employeeList.length) {
        setEmployees(employeeList);
        setIsDataAvailable(true);
      } else {
        setEmployees([]);
        setIsDataAvailable(false);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onPageChange = (e) => {
    setPage(e.page);
    setRows(e.rows);
    fetchEmployees({ pageIndex: e.page, pageSize: e.rows });
  };

  const onSortChange = (e) => {
    setSortBy(e.sortField);
    setSortOrder(e.sortOrder);
    fetchEmployees({
      sortBy: e.sortField,
      sortOrder: e.sortOrder === -1 ? "DESC" : "ASC",
    });
  };

  useEffect(() => {
    fetchEmployees({ pageIndex: 0, pageSize: 10 });
  }, [debouncedSearchTerm]);

  return {
    employeeregister: {
      activeIndex,
      onboardBtnEnable,
      nextBtnEnable,
      showOnboardBtn,
      status,
      isLeaveModal,
    },
    handleNext,
    handleBack,
    handleClearState,
    handleSave,
    loading,
    submit,
    addNewEmployee,
    handleClose,
    searchQuery,
    isDataAvailable,
    employees,
    totalRecords,
    page,
    rows,
    handleSearchChange,
    onPageChange,
    onSortChange,
    sortBy,
    sortOrder,
    debouncedSearchTerm,
  };
};

export default useEmployeeRegistry;
